import * as React from 'react';
import { isEmpty, throttle } from 'lodash';
import classNames from 'classnames';

import { FormField } from 'components/form';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Input from 'components/inputs/input/Input';
import AddNew from 'components/buttons/add-new/AddNew';

import { __t, applyTranslations } from 'utils/language';
import { isRequired, isUrl, isZalandoPage, maxLength } from 'utils/validators';
import { DELIVERABLE_TYPE_CONTENT_PRODUCTION } from 'utils/deliverables';
import { insertIf } from 'utils/list';

import type {
  Deliverable,
  DeliverableLink,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./deliverable-edit-links.i18n.json'));

type Props = {
  deliverable: Deliverable,
  links: Array<DeliverableLink>,
  label?: string,
  tooltipMessage?: string,
  isV2?: boolean,
  isZalandoLink?: boolean,
  isMultiple?: boolean,
  onEdit: (b: Array<DeliverableLink>) => void
};

type State = {
  links: Array<DeliverableLink>,
  shouldValidate: boolean
};

function shouldValidateLinks(links) {
  return links.length > 1
    || (links.length > 0
      && (
        Boolean(links[0].url.length)
        || Boolean(links[0].description)
        || Boolean(links[0].name)
      )
    );
}

const MSG_KEY = 'deliverable_edit_links';
const MAX_LINK_DESCRIPTION_LENGTH = 250;
const MAX_LINK_DESCRIPTION_LENGTH_ZALANDO_LINK = 80;
const MAX_LINK_NAME_LENGTH = 25;

/**
 * Deliverable Link Form
 * Since there is always one line to enter the data even if links array in props is empty,
 * then validation is only enabled if there is more than one element in links array
 * or if user entered something into either url or description field for first entry (this is why onBlur is needed)
 * entries key is changed in this case to enable validation, otherwise validation won't trigger
 */
class DeliverableLinksForm extends React.PureComponent<Props, State> {
  static defaultProps = {
    isMultiple: true
  };

  throttledHandleEdit: () => void;

  constructor(props: Props) {
    super(props);

    const { links } = props;

    if (!links || links.length === 0) {
      this.state = {
        links: [{
          url: '',
          description: '',
          name: ''
        }],
        shouldValidate: false
      };
    } else if (links.length > 0) {
      this.state = {
        links,
        shouldValidate: true
      };
    }

    this.throttledHandleEdit = throttle(this.handleEdit, 1000);
  }

  getUrlValidator = (link: DeliverableLink) => {
    const { shouldValidate } = this.state;
    const { isZalandoLink } = this.props;

    if (!shouldValidate) {
      return [];
    }

    return isZalandoLink ? [
      isZalandoPage(link.url),
      ...insertIf(!isEmpty(link.description) || !isEmpty(link.name), isRequired(link.url))
    ] : [
      isRequired(link.url),
      isUrl(link.url)
    ];
  };

  handleLinkFieldChange = (index: number, field: string, event: SyntheticEvent<TextEntry>) => {
    const target = event.currentTarget;
    const newValue = target.value || '';
    this.setState((state) => {
      const { links } = state;
      const newLinks = [...(links || [])];
      newLinks[index] = {
        ...newLinks[index],
        [field]: newValue
      };

      return {
        links: newLinks
      };
    }, this.throttledHandleEdit);
  };

  handleAddNewLink = () => {
    this.setState((state) => ({
      links: [...(state.links || []), {
        url: '',
        description: '',
        name: ''
      }],
      shouldValidate: true
    }), this.handleEdit);
  };

  handleRemoveLink = (index: number) => {
    this.setState((state) => {
      const newLinks = [...state.links];
      newLinks.splice(index, 1);

      return {
        links: newLinks,
        shouldValidate: shouldValidateLinks(newLinks)
      };
    }, this.handleEdit);
  };

  handleEdit = () => {
    const { links } = this.state;
    const { onEdit } = this.props;

    // if has only one link in array and it's empty - consider as no links provided
    const nonEmptyLinks = links.filter(link =>
      !isEmpty(link.url) ||
      !isEmpty(link.description) ||
      !isEmpty(link.name)
    );

    onEdit(nonEmptyLinks || []);
  };

  checkIfValidationNeeded = () => {
    this.setState((state) => ({
      shouldValidate: shouldValidateLinks(state.links)
    }), this.handleEdit);
  };

  renderLink(
    link: DeliverableLink,
    index: number,
    originalArray: Array<DeliverableLink>
  ): React.ReactNode {
    const { shouldValidate } = this.state;
    const { isV2, label, tooltipMessage, isZalandoLink, isMultiple } = this.props;

    const descriptionClass = classNames('deliverable-edit-links__description-section', {
      'deliverable-edit-links__description-section--v2': isV2,
      'deliverable-edit-links__description-section--v1': !isV2,
      'deliverable-edit-links__description-section--zalando-links': isZalandoLink && isMultiple
    });

    const urlValidator = this.getUrlValidator(link);

    return (
      <div
        className={`deliverable-edit-links deliverable-edit-links${isV2 ? '--v2' : '--v1'}`}
        key={`link-${index}-${shouldValidate.toString()}`}
      >
        <div className={isV2 ? '' : 'deliverable-edit-links__link-section'}>
          <FormField
            label={label || __t(`${MSG_KEY}.labels.link`)}
            labelType="highlighted"
            showValidationIcon={false}
            tooltipMessage={tooltipMessage}
            direction="column"
            fontSize="small"
            validators={urlValidator}
          >
            <Input
              value={link.url}
              width={isV2 ? 'responsive' : 'fixed'}
              onBlur={this.checkIfValidationNeeded}
              onChange={(event) => this.handleLinkFieldChange(index, 'url', event)}
            />
          </FormField>
        </div>
        {
          isZalandoLink && isMultiple && (
            <div className={isV2 ? '' : 'deliverable-edit-links__link-section'}>
              <FormField
                label={__t(`${MSG_KEY}.labels.link_name`)}
                labelType="highlighted"
                showValidationIcon={false}
                direction="column"
                fontSize="small"
                validators={[
                  (link.url || link.description) ? isRequired(link.name) : { valid: true, message: '' },
                  maxLength(link.name, MAX_LINK_NAME_LENGTH)
                ]}
              >
                <Input
                  value={link.name || ''}
                  width={isV2 ? 'responsive' : 'fixed'}
                  onBlur={this.checkIfValidationNeeded}
                  charCount={{
                    curCount: link.name?.length || 0,
                    maxChars: MAX_LINK_NAME_LENGTH
                  }}
                  onChange={(event) => this.handleLinkFieldChange(index, 'name', event)}
                />
              </FormField>
            </div>
          )
        }
        <div className={descriptionClass}>
          <FormField
            label={__t(`${MSG_KEY}.labels.description`)}
            labelType="highlighted"
            showValidationIcon={false}
            direction="column"
            fontSize="small"
            validators={[
              maxLength(
                link.description,
                isZalandoLink ? MAX_LINK_DESCRIPTION_LENGTH_ZALANDO_LINK : MAX_LINK_DESCRIPTION_LENGTH
              )
            ]}
          >
            <Input
              value={link.description}
              width="responsive"
              onBlur={this.checkIfValidationNeeded}
              onChange={(event) => this.handleLinkFieldChange(index, 'description', event)}
              charCount={isV2 ? {
                curCount: link.description.length,
                maxChars: isZalandoLink ?
                  MAX_LINK_DESCRIPTION_LENGTH_ZALANDO_LINK : MAX_LINK_DESCRIPTION_LENGTH
              } : undefined}
            />
          </FormField>
          {originalArray.length > 1 && (
            <div className="deliverable-edit-links__action-section">
              <div
                className="deliverable-edit-links__action-remove"
                onClick={() => this.handleRemoveLink(index)}
              >
                <SVGDynamicIcon
                  name="cross-custom-fg"
                  customColor="grey"
                  width={14}
                  height={14}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { deliverable, isV2, isMultiple } = this.props;
    const { links } = this.state;
    const deliverableType = deliverable.deliverableType;

    if (deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION) {
      // links are not shown for content production task
      return null;
    }

    const linksToRender = (links && links.length > 0) ? links : [{
      url: '',
      description: '',
      name: ''
    }];

    return (
      <React.Fragment>
        {linksToRender.map(this.renderLink, this)}
        {
          isMultiple && (
            <AddNew
              text={__t(`${MSG_KEY}.labels.add_another_link`)}
              icon={{
                name: 'plus-inside-circle-custom-bg-white-fg',
                width: 16,
                height: 16,
                distance: 'double'
              }}
              {...(!isV2 ? {} : { color: 'grey', size: 'small' })}
              onClick={this.handleAddNewLink}
            />
          )
        }
      </React.Fragment>
    );
  }
}

export default DeliverableLinksForm;
